import { DEFAULT_VARIANT_TITLE } from "@smartrr/shared/constants";
import { IPurchaseState, IUnavailableVariantsAndDeliveries } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { useVariantToPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import isNumber from 'lodash/isNumber';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from "react";

import { useCleanedAndSortedPurchables } from "@customer-app/state/reducers/purchasables";

export function useUnavailableItemBannerCopy(
  li: IPurchaseStateLineItem,
  delivery?: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>
) {
  const cleanedAndSortedPurchasables = useCleanedAndSortedPurchables().purchasables;

  let displayString = "";

  if (!delivery || !li) {
    return displayString;
  }

  const purchaseable = cleanedAndSortedPurchasables.find(p => p.vnts?.some(vnt => vnt.id === li.vnt.id));

  if (!purchaseable) {
    return displayString;
  }

  const { schedule } = delivery.purchaseState;
  const isPrepaid = schedule.paymentFrequencyMultiple !== schedule.deliveryFrequencyMultiple;
  const isAddon = li.isAddOn;

  // onRemove is only available for addons and sub items IF there are more than 1 sub item
  const isOnRemoveAvailable =
    isAddon ||
    (delivery?.purchaseState?.stLineItems.filter(lineItem => !lineItem.isAddOn && !lineItem.isRedeemed).length >
      1 &&
      delivery?.purchaseState?.stLineItems.some(lineItem => lineItem.id !== li.id));

  const nameToDisplay = `${purchaseable ? purchaseable.purchasableName : ""}${
    li.vnt.purchasableVariantName === DEFAULT_VARIANT_TITLE ? "" : ` - ${li.vnt.purchasableVariantName}`
  }`;

  displayString += typedLocalize("banner.oosVariantMessage", {
    variant: nameToDisplay,
  });

  if (isPrepaid) {
    // if delivery is prepaid
    displayString += ` ${typedLocalize("banner.cancelSubscriptionMessage")}`;
  } else if (isOnRemoveAvailable && isAddon) {
    // if line item is addon and > 1 subscription item exists in the purchase state
    displayString += ` ${typedLocalize("banner.removeVariantMessage")}`;
  } else if (isOnRemoveAvailable) {
    // if line item is subscription item and > 1 subscription item exists in the purchase state
    displayString += ` ${typedLocalize("banner.swapOrRemoveVariantMessage")}`;
  } else {
    // if line item is subscription item and only 1 subscription item exists in the purchase state
    displayString += ` ${typedLocalize("banner.swapVariantMessage")}`;
  }

  return displayString;
}

export function useUnavailableLineItems(
  unavailableVariantsAndDeliveries: IUnavailableVariantsAndDeliveries,
  stLineItems: IPurchaseStateLineItem[],
  skdIdx: number
) {
  const arr: IPurchaseStateLineItem[] = [];
  const { purchasables: unavailablePurchasables, vnts: unavailableVariants } = unavailableVariantsAndDeliveries;
  const purchasables = useCleanedAndSortedPurchables().purchasables;
  const variantToPurchasableMap = useVariantToPurchasableMap(purchasables);

  return useMemo(() => {
    if (!stLineItems.length) {
      return [];
    }

    for (const lineItem of stLineItems) {
      const isAddon = lineItem.isAddOn;

      const isPurchasableUnavailable = (unavailablePurchasables ?? []).some(
        p => p.id === (variantToPurchasableMap[lineItem.vnt.id]?.id ?? "")
      );

      const isVariantUnavailable = (unavailableVariants ?? []).some(v => v.id === lineItem.vnt.id);

      if (
        (isPurchasableUnavailable || isVariantUnavailable) &&
        (isAddon && isNumber(lineItem.skdIdx) ? lineItem.skdIdx >= skdIdx : true) // Capturing addons that are in current or future deliveries
      ) {
        arr.push(lineItem);
      }
    }

    return uniqBy(arr, "id");
  }, [stLineItems.length, skdIdx, unavailableVariantsAndDeliveries]);
}
